// Editable site data — replace freely as the business grows.

export const BUSINESS = {
  name: "Dillpickle's Carwash",
  tagline: "Professional Mobile Car Detailing That Comes to You.",
  city: "Atlanta",
  phone: "404-858-9811",
  phoneHref: "tel:+14048589811",
  email: "dylanhlibowsky@icloud.com",
  emailHref: "mailto:dylanhlibowsky@icloud.com",
  instagram: "https://instagram.com",
  tiktok: "https://tiktok.com",
};

// Vehicle sizes
export const VEHICLE_SIZES = [
  { id: "small", label: "Small Car", short: "Small Car", icon: "car" },
  { id: "midsize", label: "Midsize Car / SUV", short: "Midsize", icon: "car" },
  { id: "large", label: "Large SUV / Truck", short: "Large", icon: "truck" },
];

// Services — exact list & exact prices (no multipliers)
export const SERVICES = [
  {
    id: "express-exterior",
    name: "Express Exterior Wash",
    duration: "30–45 min",
    blurb: "Foam bath, hand wash, wheels & tires dressed, windows shined. Quick and pristine.",
    features: [
      "Two-bucket foam hand wash",
      "Wheels, tires & rims detailed",
      "Streak-free exterior windows",
      "Tire shine finish",
    ],
    prices: { small: 35, midsize: 40, large: 45 },
    accent: "from-[#7CB342]/20 to-transparent",
  },
  {
    id: "express-interior",
    name: "Express Interior Wash",
    duration: "30–45 min",
    blurb: "Vacuum, wipe-down, and shine — a quick interior reset between full details.",
    features: [
      "Full interior vacuum",
      "Dash, console & door wipe-down",
      "Interior windows",
      "Air freshener finish",
    ],
    prices: { small: 30, midsize: 40, large: 50 },
    accent: "from-[#7CB342]/15 to-transparent",
  },
  {
    id: "interior-deep",
    name: "Interior Deep Clean",
    duration: "2–3 hrs",
    blurb: "Pet hair, spills, kid-mess — gone. Steam, shampoo, and deep extraction.",
    features: [
      "Carpet & seat shampoo + extraction",
      "Pet hair removal",
      "Stain & odor neutralization",
      "Vents, crevices & headliner",
      "Leather clean & condition",
    ],
    prices: { small: 85, midsize: 95, large: 105 },
    accent: "from-[#E53935]/12 to-transparent",
  },
  {
    id: "interior-exterior",
    name: "Interior & Exterior Wash",
    duration: "1.5–2 hrs",
    blurb: "Our flagship. The complete inside-and-out detail your car deserves.",
    features: [
      "Everything in Express Exterior",
      "Everything in Express Interior",
      "Door jambs & frames cleaned",
      "Interior + exterior windows",
      "Final hand-dry & inspection",
    ],
    prices: { small: 50, midsize: 60, large: 70 },
    accent: "from-[#7CB342]/25 to-transparent",
    popular: true,
  },
];

export const TIME_SLOTS = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM",
];

// Hours. Day-of-week index = JS .getDay() (0=Sun ... 6=Sat).
// closed=true means the day is unavailable for booking.
export const HOURS = [
  { day: "Sunday", closed: true },
  { day: "Monday", closed: false, range: "2:00 PM – 6:00 PM" },
  { day: "Tuesday", closed: false, range: "2:00 PM – 6:00 PM" },
  { day: "Wednesday", closed: false, range: "2:00 PM – 6:00 PM" },
  { day: "Thursday", closed: false, range: "2:00 PM – 6:00 PM" },
  { day: "Friday", closed: true },
  { day: "Saturday", closed: false, range: "8:00 AM – 2:00 PM" },
];

// Bookable start times per weekday (each detail needs ~1.5 hrs).
// Mon–Thu = afternoon (2–6 PM), Sat = morning (8 AM–2 PM), Fri/Sun closed.
const WEEKDAY_SLOTS = ["2:00 PM", "3:00 PM", "4:00 PM"];
const SATURDAY_SLOTS = ["8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM"];
export const DAY_SLOTS = {
  0: [],              // Sunday - closed
  1: WEEKDAY_SLOTS,   // Monday
  2: WEEKDAY_SLOTS,   // Tuesday
  3: WEEKDAY_SLOTS,   // Wednesday
  4: WEEKDAY_SLOTS,   // Thursday
  5: [],              // Friday - closed
  6: SATURDAY_SLOTS,  // Saturday
};

// One-off open exception days get their own explicit slot lists.
export const DATE_SLOT_OVERRIDES = {
  "2026-07-31": ["8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM"], // Fri special, morning-only
  "2026-08-02": ["8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM"], // Sun special
};

const _iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const slotsForDate = (date) => {
  if (!date) return [];
  const override = DATE_SLOT_OVERRIDES[_iso(date)];
  if (override) return override;
  return DAY_SLOTS[date.getDay()] || [];
};

export const isOpenDay = (date) => {
  if (!date) return false;
  return !HOURS[date.getDay()].closed;
};

// Months (1-12) when bookings are closed entirely (e.g. owner away).
// Empty array = always open.
export const BLACKOUT_MONTHS = [7]; // July — owner unavailable

// Specific dates (YYYY-MM-DD) that are OPEN even if they'd normally be
// closed (a Friday/Sunday or inside a blackout month). One-off exceptions.
export const OPEN_EXCEPTIONS = ["2026-07-31", "2026-08-02"];

export const isBlackoutDate = (date) => {
  if (!date) return false;
  return BLACKOUT_MONTHS.includes(date.getMonth() + 1);
};

export const TRUST_BADGES = [
  { title: "5-Star Rated", desc: "Loved across Atlanta", icon: "star" },
  { title: "Satisfaction Guaranteed", desc: "We don't leave 'til it shines", icon: "shield-check" },
  { title: "Fully Mobile", desc: "We bring everything to you", icon: "truck" },
  { title: "Pro Equipment", desc: "Hot water, extractors, the works", icon: "zap" },
  { title: "Fast Response", desc: "Same-week booking, often same-day", icon: "clock" },
];

export const REVIEWS = [];

export const FAQS = [
  { q: "Where do you provide service?", a: "I come to you!" },
  { q: "Do you bring your own water and power?", a: "No, but that is all I need from you to make your car look perfect." },
  { q: "How long does a detail take?", a: "Depending on the state of the car and its size. A normal car wash can take anywhere from 1½–2 hours, but a detail can take up to 4 hours." },
  { q: "What if I'm not happy with the result?", a: "If you are not happy with the results, call me and I'll make it right within 24 hours for free." },
  { q: "How do I pay?", a: "You can Venmo, Apple Pay, card, or cash." },
  { q: "Can I book for a fleet or multiple vehicles?", a: "I can do up to 2 cars per day." },
];

// Our Work gallery — real client photos paired Before/After.
export const GALLERY = [
  {
    title: "Range Rover Sport",
    service: "Express Exterior Wash",
    before: "/gallery/rangerover-after.jpg",
    after: "/gallery/rangerover-before.jpg",
  },
  {
    title: "Mustang Convertible",
    service: "Express Exterior Wash",
    before: "/gallery/mustang-before.jpg",
    after: "/gallery/mustang-after.jpg",
  },
  {
    title: "Wheel & Tire Detail",
    service: "Express Exterior Wash",
    before: "https://customer-assets.emergentagent.com/job_dillpickle-detail/artifacts/hvm587xv_IMG_0073.jpg",
    after: "https://customer-assets.emergentagent.com/job_dillpickle-detail/artifacts/yivpwyw3_IMG_0080.jpg",
  },
  {
    title: "Mustang Floor & Mats",
    service: "Interior Deep Clean",
    before: "https://customer-assets.emergentagent.com/job_dillpickle-detail/artifacts/mgp4dao4_IMG_1850.jpg",
    after: "https://customer-assets.emergentagent.com/job_dillpickle-detail/artifacts/mhumy5qp_IMG_1852.jpeg",
  },
  {
    title: "Mustang Door Sill",
    service: "Interior Deep Clean",
    before: "/door-sill-before.jpg",
    after: "https://customer-assets.emergentagent.com/job_dillpickle-detail/artifacts/bg18ls3k_IMG_1853.jpg",
  },
  {
    title: "Honda Pilot Interior",
    service: "Interior Deep Clean",
    before: "https://customer-assets.emergentagent.com/job_dillpickle-detail/artifacts/rqqfuknf_IMG_2011.jpg",
    after: "https://customer-assets.emergentagent.com/job_dillpickle-detail/artifacts/0jxa6mmm_IMG_2013.jpg",
  },
];

// Price lookup helpers
export const priceFor = (service, vehicleSizeId) => {
  if (!service || !service.prices) return 0;
  return service.prices[vehicleSizeId] ?? Math.min(...Object.values(service.prices));
};

export const priceFromLow = (service) => {
  if (!service || !service.prices) return 0;
  return Math.min(...Object.values(service.prices));
};

// Back-compat for any existing import paths
export const computePrice = (_baseUnused, vehicleSizeId, serviceArg) => {
  // computePrice(service.base, sizeId) old signature kept as a no-op fallback
  if (serviceArg) return priceFor(serviceArg, vehicleSizeId);
  return 0;
};
